"use client"; // Modal are always rendered client-side

import type { PropsWithChildren, ReactNode } from "react";

import { usePathname } from "next/navigation";
import { createContext, useContext, useMemo, useState } from "react";

import { ModalClient } from "@/src/components/ui/Modal/ModalClient";

interface ModalContextType {
  setModal: (modal: ReactNode) => void;
  setModalContainer: (container: HTMLElement | null) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: PropsWithChildren): ReactNode {
  const [modal, setModal] = useState<ReactNode>(null);
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(
    null,
  );
  const modalContext = useMemo(
    () => ({ setModal, setModalContainer }),
    [setModal, setModalContainer],
  );

  function closeModal(): void {
    setModal(null);
  }

  // Close modal on page change
  const pathname = usePathname();
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    closeModal();
  }

  return (
    <ModalContext.Provider value={modalContext}>
      {children}
      <ModalClient container={modalContainer} onClose={closeModal}>
        {modal}
      </ModalClient>
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextType {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within ModalProvider");
  }
  return context;
}
